import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Customer, Reservation } from '../../types';
import { getCustomerById, mapApiCustomer, deleteCustomer } from '../../services/customerService';
import {
  getReservationsByCustomerId,
  unlinkReservationCustomer,
  mapApiToGridReservations,
  ApiReservation,
} from '../../services/reservationService';
import { useModificationHistory } from '../../services/useModificationHistory';
import ModificationHistorySection from '../reservations/ModificationHistorySection';
import { formatDate, formatDateRange, isFuture, isPast } from '../../utils/dateUtils';
import { formatPhone, formatCurrency, formatStatus, getStatusColor, getPaymentStatusColor } from '../../utils/formatters';
import Modal from '../shared/Modal';
import CustomerFormModal from './CustomerFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import AdminCancellationWizard from '../reservations/CancellationWizard/AdminCancellationWizard';
import ModifyReservationWizard from '../reservations/ModifyReservationWizard';
import PaymentPopup from '../reservations/PaymentPopup';
import { fetchPaymentsForReservation, PaymentRecord } from '../../services/paymentService';
import { authService } from '../../services/auth';
import './MemberCard.css';

const CustomerCard: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservations, setReservations] = useState<ApiReservation[]>([]);
  const [displayReservations, setDisplayReservations] = useState<Reservation[]>([]);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCancelWizard, setShowCancelWizard] = useState(false);
  const [showModifyWizard, setShowModifyWizard] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [modalPayments, setModalPayments] = useState<PaymentRecord[]>([]);
  const [modalPaymentLoading, setModalPaymentLoading] = useState(false);

  // Modification history (shared hook)
  const modHistory = useModificationHistory(selectedReservation, modalPayments);

  useEffect(() => {
    if (!customerId) return;
    loadCustomerData(parseInt(customerId, 10));
  }, [customerId]);

  const loadModalPayments = useCallback(() => {
    if (!selectedReservation) {
      setModalPayments([]);
      return;
    }
    const storeId = authService.getStoreId();
    if (!storeId) return;
    setModalPaymentLoading(true);
    const fetches = [fetchPaymentsForReservation(selectedReservation.id, storeId)];
    if (selectedReservation.originalReservationId) {
      fetches.push(fetchPaymentsForReservation(selectedReservation.originalReservationId, storeId));
    }
    Promise.all(fetches)
      .then((results) => {
        const merged = results.flat();
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        setModalPayments(unique);
      })
      .catch((err) => console.error('Payment fetch error:', err))
      .finally(() => setModalPaymentLoading(false));
  }, [selectedReservation]);

  useEffect(() => {
    loadModalPayments();
  }, [loadModalPayments]);

  const loadCustomerData = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const [apiCustomer, apiReservations] = await Promise.all([
        getCustomerById(id),
        getReservationsByCustomerId(id),
      ]);
      setCustomer(mapApiCustomer(apiCustomer));

      const mapped = mapApiToGridReservations(apiReservations)
        .sort((a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime());
      setDisplayReservations(mapped);
    } catch (err: any) {
      console.error('Error loading customer data:', err);
      setError(err.message || 'Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (!customer) return;
    try {
      const res = await getReservationsByCustomerId(customer.id);
      setReservations(res);
    } catch {
      setReservations([]);
    }
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customer) return;
    setDeleteLoading(true);
    try {
      // Unlink reservations first
      for (const res of reservations) {
        await unlinkReservationCustomer(res.id);
      }
      await deleteCustomer(customer.id);
      navigate('/members?tab=customers');
    } catch (err: any) {
      console.error('Error deleting customer:', err);
      alert(err.message || 'Failed to delete customer');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStoreId = (): number => {
    const userData = localStorage.getItem('managerUserData');
    if (userData) return JSON.parse(userData).store_id || 0;
    return 0;
  };

  const futureReservations = useMemo(
    () => displayReservations.filter(r => isFuture(r.checkIn) || r.status === 'checked_in'),
    [displayReservations]
  );
  const pastReservations = useMemo(
    () => displayReservations.filter(r => isPast(r.checkIn) && r.status !== 'checked_in'),
    [displayReservations]
  );

  if (loading) {
    return (
      <div className="member-card-page">
        <div className="member-card-header">
          <Link to="/members?tab=customers" className="back-link">&larr; Back to Customers</Link>
        </div>
        <div className="no-results"><p>Loading customer data...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="member-card-page">
        <div className="member-card-header">
          <Link to="/members?tab=customers" className="back-link">&larr; Back to Customers</Link>
        </div>
        <div className="no-results">
          <p>{error}</p>
          <button
            onClick={() => customerId && loadCustomerData(parseInt(customerId, 10))}
            className="btn btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="member-not-found">
        <h2>Customer Not Found</h2>
        <p>The requested customer could not be found.</p>
        <Link to="/members?tab=customers" className="back-link">&larr; Back to Customers</Link>
      </div>
    );
  }

  const handleEditReservation = () => {
    setShowModifyWizard(true);
  };

  const handleCancelReservation = () => {
    setShowCancelWizard(true);
  };

  const handleDeleteReservation = () => {
    alert('Delete reservation functionality coming soon.');
  };

  const handleWizardSuccess = () => {
    setSelectedReservation(null);
    setShowCancelWizard(false);
    setShowModifyWizard(false);
    if (customerId) loadCustomerData(parseInt(customerId, 10));
  };

  const renderReservationModal = () => {
    if (!selectedReservation) return null;

    const canModify = isFuture(selectedReservation.checkIn) || selectedReservation.status === 'checked_in';

    const footer = (
      <>
        <button className="btn btn-secondary" onClick={() => setSelectedReservation(null)}>
          Close
        </button>
        {canModify && (
          <>
            <button className="btn btn-warning" onClick={handleEditReservation}>
              EDIT
            </button>
            <button className="btn btn-danger" onClick={handleCancelReservation}>
              CANCEL
            </button>
            <button className="btn btn-danger" onClick={handleDeleteReservation}>
              DELETE
            </button>
          </>
        )}
      </>
    );

    return (
      <Modal
        isOpen={true}
        onClose={() => setSelectedReservation(null)}
        title={`Reservation ${selectedReservation.confirmationCode}`}
        size="medium"
        footer={footer}
      >
        <div className="reservation-detail">
          <div className="detail-status">
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(selectedReservation.status) }}
            >
              {formatStatus(selectedReservation.status)}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-item">
              <label>Dates</label>
              <span>{formatDateRange(selectedReservation.checkIn + 'T12:00:00', selectedReservation.checkOut + 'T12:00:00')}</span>
            </div>
            <div className="detail-item">
              <label>Site</label>
              <span>{selectedReservation.siteAlias}</span>
            </div>
            <div className="detail-item">
              <label>Nights</label>
              <span>{selectedReservation.days}</span>
            </div>
            <div className="detail-item">
              <label>Guests</label>
              <span>{selectedReservation.adults} adults, {selectedReservation.children} children</span>
            </div>
            <div className="detail-item">
              <label>Pets</label>
              <span>{selectedReservation.pets}</span>
            </div>
            <div className="detail-item">
              <label>Vehicles</label>
              <span>{selectedReservation.vehicles}</span>
            </div>
            <div className="detail-item">
              <label>Total Cost</label>
              <span>{formatCurrency(selectedReservation.totalCost)}</span>
            </div>
            <div className="detail-item">
              <label>Payment Status</label>
              {modalPaymentLoading ? (
                <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
              ) : modalPayments.length > 0 ? (
                <span style={{ color: getPaymentStatusColor(modalPayments[0].status) }}>
                  {formatStatus(modalPayments[0].status)}
                </span>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>No Payments</span>
              )}
            </div>
          </div>

          <div className="detail-item">
            <span
              className="view-payment-link"
              onClick={() => setShowPaymentPopup(true)}
            >
              View Payment Details &rarr;
            </span>
          </div>

          {selectedReservation.specialRequests && (
            <div className="detail-requests">
              <label>Special Requests</label>
              <p>{selectedReservation.specialRequests}</p>
            </div>
          )}

          {/* Modification History */}
          <ModificationHistorySection
            {...modHistory}
            reservation={selectedReservation ?? undefined}
            onPaymentsChanged={loadModalPayments}
          />

          {!canModify && (
            <div className="detail-notice">
              This reservation is in the past and cannot be modified.
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div className="member-card-page">
      <div className="member-card-header">
        <Link to="/members?tab=customers" className="back-link">&larr; Back to Customers</Link>
        <div className="card-header-row">
          <h1 className="page-title">{customer.firstName} {customer.lastName}</h1>
          <div className="card-header-actions">
            <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
              Edit
            </button>
            <button className="btn btn-danger" onClick={handleDeleteClick}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="member-card-content">
        <section className="member-details-section">
          <h2 className="section-title">Customer Information</h2>
          <div className="member-details-card">
            <div className="details-grid">
              <div className="detail-item">
                <label>Email</label>
                <span>{customer.email}</span>
              </div>
              <div className="detail-item">
                <label>Phone</label>
                <span>{customer.mainPhone ? formatPhone(customer.mainPhone) : '-'}</span>
              </div>
              {customer.secondPhone && (
                <div className="detail-item">
                  <label>Secondary Phone</label>
                  <span>{formatPhone(customer.secondPhone)}</span>
                </div>
              )}
              <div className="detail-item">
                <label>Address</label>
                <span>
                  {customer.address ? (
                    <>
                      {customer.address}<br />
                      {customer.city}, {customer.state} {customer.zipCode}
                      {customer.country && `, ${customer.country}`}
                    </>
                  ) : '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>Created</label>
                <span>{customer.createdAt ? formatDate(customer.createdAt) : '-'}</span>
              </div>
              <div className="detail-item">
                <label>Status</label>
                <span className={`status-badge status-badge--${customer.banned ? 'inactive' : 'active'}`}>
                  {customer.banned ? 'Banned' : 'Active'}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Reservation History Section */}
        <section className="reservation-history-section">
          <h2 className="section-title">Reservation History</h2>

          {displayReservations.length === 0 ? (
            <p className="no-reservations">No reservations found for this customer.</p>
          ) : (
            <>
              {futureReservations.length > 0 && (
                <div className="reservation-group">
                  <h3 className="group-title">Upcoming & Current Reservations</h3>
                  <div className="reservation-list">
                    {futureReservations.map(reservation => (
                      <div
                        key={`${reservation.id}-${reservation.siteId}`}
                        className="reservation-item reservation-item--upcoming"
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        <div className="reservation-dates">
                          <span className="date-range">
                            {formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}
                          </span>
                          <span className="nights">{reservation.days} nights</span>
                        </div>
                        <div className="reservation-site">
                          Site {reservation.siteAlias}
                        </div>
                        <div className="reservation-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {formatStatus(reservation.status)}
                          </span>
                        </div>
                        <div className="reservation-cost">
                          {formatCurrency(reservation.totalCost)}
                        </div>
                        <div className="reservation-actions">
                          <span className="view-details">View Details &rarr;</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pastReservations.length > 0 && (
                <div className="reservation-group">
                  <h3 className="group-title">Past Reservations</h3>
                  <div className="reservation-list">
                    {pastReservations.map(reservation => (
                      <div
                        key={`${reservation.id}-${reservation.siteId}`}
                        className="reservation-item reservation-item--past"
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        <div className="reservation-dates">
                          <span className="date-range">
                            {formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}
                          </span>
                          <span className="nights">{reservation.days} nights</span>
                        </div>
                        <div className="reservation-site">
                          Site {reservation.siteAlias}
                        </div>
                        <div className="reservation-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {formatStatus(reservation.status)}
                          </span>
                        </div>
                        <div className="reservation-cost">
                          {formatCurrency(reservation.totalCost)}
                        </div>
                        <div className="reservation-actions">
                          <span className="view-details">View Details &rarr;</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {renderReservationModal()}

      <CustomerFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSaved={() => customerId && loadCustomerData(parseInt(customerId, 10))}
        customer={customer}
        storeId={getStoreId()}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        entityType="customer"
        entityName={`${customer.firstName} ${customer.lastName}`}
        reservationCount={reservations.length}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleteLoading}
      />

      <AdminCancellationWizard
        isOpen={showCancelWizard}
        onClose={() => setShowCancelWizard(false)}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      <ModifyReservationWizard
        isOpen={showModifyWizard}
        onClose={() => setShowModifyWizard(false)}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      {showPaymentPopup && selectedReservation && (
        <PaymentPopup
          reservation={selectedReservation}
          onClose={() => setShowPaymentPopup(false)}
        />
      )}
    </div>
  );
};

export default CustomerCard;
